import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Info } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import indexPriority from 'theme/z-index';

const FEE_EXPLANATION =
    'A cobrança da taxa de serviço contribui com custos operacionais, como ' +
    'desenvolvimento, atualização e suporte, além de viabilizar investimentos ' +
    'contínuos em segurança e inovação.';

export default function ServiceFeeInfo({ label = 'Entenda nossa taxa', align = 'center' }) {
    const [open, setOpen] = useState(false);
    const isTouch = useRef(false);
    const wrapperRef = useRef(null);

    // Hover drives it on pointer devices; tap toggles it on touch.
    const handleTouchStart = () => { isTouch.current = true; };
    const handleMouseEnter = () => { if (!isTouch.current) setOpen(true); };
    const handleMouseLeave = () => { if (!isTouch.current) setOpen(false); };
    const handleClick = () => { if (isTouch.current) setOpen(prev => !prev); };

    // On touch, an outside tap dismisses the bubble.
    useEffect(() => {
        if (!open) return undefined;

        const handleOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
    }, [open]);

    return (
        <Wrapper ref={wrapperRef} align={align}>
            <Trigger
                type='button'
                aria-expanded={open}
                onTouchStart={handleTouchStart}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
            >
                <Info size={14} strokeWidth={2.25} />
                <TriggerLabel>{label}</TriggerLabel>
            </Trigger>

            <Bubble open={open} role='tooltip' onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <BubbleHeader>
                    <Info size={13} strokeWidth={2.5} />
                    <span>Taxa de serviço</span>
                </BubbleHeader>
                <BubbleText>{FEE_EXPLANATION}</BubbleText>
            </Bubble>
        </Wrapper>
    );
}

const lift = keyframes`
    from { opacity: 0; transform: translate(-50%, 4px); }
    to   { opacity: 1; transform: translate(-50%, 0); }
`;

const Wrapper = styled.div`
    position: relative;
    display: inline-flex;
    width: max-content;
    margin: 10px auto 0;

    ${({ align }) => align === 'start' && `
        margin-left: 0;
        margin-right: auto;
    `}
`;

const Trigger = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 0;
    padding: 2px 0;
    border: none;
    background: transparent;
    color: ${colors.night};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: color .2s ease;

    svg { color: ${colors.occasionalPurple}; flex-shrink: 0; }

    &:hover { color: ${colors.occasionalPurple}; }
`;

const TriggerLabel = styled.span`
    border-bottom: 1px dashed ${colors.nightLight};
    line-height: 1.1;
    padding-bottom: 1px;
    transition: border-color .2s ease;

    ${Trigger}:hover & {
        border-color: ${colors.occasionalPurple};
    }
`;

const Bubble = styled.div`
    position: absolute;
    bottom: calc(100% + 11px);
    left: 50%;
    transform: translate(-50%, 4px);
    width: 290px;
    max-width: 78vw;
    padding: 13px 15px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    box-shadow: 0 12px 30px -10px rgba(26, 26, 26, .28), 0 2px 6px rgba(26, 26, 26, .06);
    text-align: left;
    z-index: ${indexPriority.tremendous};
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity .2s ease, transform .2s ease, visibility .2s;

    /* Soft purple accent edge */
    &::before {
        content: '';
        position: absolute;
        top: 13px;
        bottom: 13px;
        left: 0;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: ${colors.occasionalPurple};
    }

    /* Caret */
    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        margin-top: -5px;
        width: 10px;
        height: 10px;
        background: ${colors.neutralLight};
        border-right: 1px solid ${colors.neutralDark};
        border-bottom: 1px solid ${colors.neutralDark};
        border-bottom-right-radius: 2px;
    }

    ${({ open }) => open && css`
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: translate(-50%, 0);
        animation: ${lift} .22s ease;
    `}

    @media screen and (max-width: ${breakpoints.smd}) {
        width: 260px;
    }
`;

const BubbleHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    color: ${colors.occasionalPurple};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .2px;

    svg { flex-shrink: 0; }
`;

const BubbleText = styled.p`
    margin: 0;
    color: ${colors.coralGrey};
    font-size: 12.5px;
    line-height: 1.55;
`;
